import React, { useEffect, useState, useCallback } from 'react';
import { CustomBreadcrumb, CustomButton, CustomModal } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn } from '@/shared/components/molecules';
import { Home, Mail, Eye, Search, X, Calendar, User, Building, RotateCcw } from 'lucide-react';
import { contactApi } from '../api/contactApi';
import { ContactInquiry } from '../types';
import { useToastStore } from '@/shared/stores/toastStore';

export const ListContacts = () => {
    const [contacts, setContacts] = useState<ContactInquiry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);
    const [search, setSearch] = useState('');
    const { showToast } = useToastStore();

    // Modal details view state
    const [selectedInquiry, setSelectedInquiry] = useState<ContactInquiry | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchContacts = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await contactApi.getContacts({ page, page_size: pageSize });
            if (response.success && response.data) {
                const data = response.data;
                let items: ContactInquiry[] = [];
                if (Array.isArray(data)) {
                    items = data;
                    setTotalCount(data.length);
                    setTotalPages(1);
                    setHasNext(false);
                    setHasPrevious(false);
                } else {
                    items = data.items || [];
                    const totalVal = data.total !== undefined ? data.total : (data.total_count || 0);
                    setTotalCount(totalVal);
                    const computedTotalPages = data.total_pages !== undefined 
                        ? data.total_pages 
                        : Math.max(1, Math.ceil(totalVal / (data.page_size || pageSize || 10)));
                    setTotalPages(computedTotalPages);
                    setHasNext(data.has_next || false);
                    setHasPrevious(data.has_previous || false);
                }
                setContacts(items);
            } else {
                showToast(response.error || 'Failed to load contact messages', 'error');
            }
        } catch (error) {
            showToast('Failed to load contact messages', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [page, pageSize, showToast]);

    useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);

    const handleOpenDetails = (inquiry: ContactInquiry) => {
        setSelectedInquiry(inquiry);
        setIsModalOpen(true);
    };

    // Filter contacts locally based on search input
    const filteredContacts = contacts.filter((item) => {
        const term = search.toLowerCase().trim();
        if (!term) return true;
        return (
            (item.name || '').toLowerCase().includes(term) ||
            (item.email || '').toLowerCase().includes(term) ||
            (item.school_name || '').toLowerCase().includes(term) ||
            (item.subject || '').toLowerCase().includes(term) ||
            (item.message || '').toLowerCase().includes(term)
        );
    });

    const columns: CustomTableColumn<ContactInquiry>[] = [
        {
            header: 'Sender Name',
            key: 'name',
            width: '20%',
            minWidth: '180px',
            render: (item) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {item.name || 'Anonymous'}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {item.email}
                    </span>
                </div>
            )
        },
        {
            header: 'School / Organization',
            key: 'school_name',
            width: '20%',
            minWidth: '160px',
            render: (item) => (
                <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                    {item.school_name || '-'}
                </span>
            )
        },
        {
            header: 'Subject',
            key: 'subject',
            width: '18%',
            minWidth: '140px',
            render: (item) => (
                <span style={{
                    display: 'inline-flex',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    color: 'var(--accent-primary)',
                    fontWeight: 500
                }}>
                    {item.subject || 'General'}
                </span>
            )
        },
        {
            header: 'Message Snippet',
            key: 'message',
            width: '25%',
            minWidth: '200px',
            render: (item) => {
                const limit = 65;
                const text = item.message || '';
                return (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        {text.length > limit ? text.slice(0, limit) + '...' : text}
                    </span>
                );
            }
        },
        {
            header: 'Received On',
            key: 'created_at',
            width: '12%',
            minWidth: '120px',
            render: (item) => (
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {new Date(item.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
            )
        },
        {
            header: 'Actions',
            key: 'id',
            align: 'right',
            width: '5%',
            minWidth: '70px',
            render: (item) => (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleOpenDetails(item); }}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.5rem',
                            color: 'var(--accent-primary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px',
                            transition: 'all 0.2s'
                        }}
                        title="View Details"
                        onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-tertiary, rgba(0,0,0,0.05))'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                        onFocus={(e) => e.currentTarget.style.background = 'var(--bg-tertiary, rgba(0,0,0,0.05))'}
                        onBlur={(e) => e.currentTarget.style.background = 'none'}
                    >
                        <Eye size={18} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="page-container">
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/super-admin', icon: <Home size={14} /> },
                    { label: 'Contact Messages', icon: <Mail size={14} /> }
                ]}
            />
            <CustomCommonScreenHeader
                title="Contact Inquiries"
                subtitle="View inquiries and messages submitted by visitors through the public portal."
                icon={<Mail size={24} />}
                actionLabel="Refresh"
                actionIcon={<RotateCcw size={16} />}
                onActionClick={fetchContacts}
            />

            {/* Action Bar / Responsive Search Input */}
            <div className="table-filter-bar">
                <div className="table-search-box">
                    <Search size={16} className="table-search-icon" />
                    <input
                        type="text"
                        placeholder="Search inquiries..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button
                            className="table-search-clear"
                            onClick={() => setSearch('')}
                            title="Clear search"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
            </div>

            {/* Main Inquiries Table with Unified Pagination */}
            <CustomTable
                columns={columns}
                data={filteredContacts}
                isLoading={isLoading}
                onRowClick={handleOpenDetails}
                isCompact={true}
                emptyMessage="No contact inquiries found"
                emptyIcon={<Mail size={32} />}
                pagination={{
                    currentPage: page,
                    totalPages: totalPages,
                    totalCount: search ? filteredContacts.length : totalCount,
                    pageSize: pageSize,
                    onPageChange: setPage,
                    hasPrevious: hasPrevious,
                    hasNext: hasNext,
                    isLoading: isLoading,
                    itemName: 'inquiries',
                }}
            />

            {/* View Details Inquiry Modal */}
            <CustomModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Contact Inquiry Details"
                size="medium"
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                        <CustomButton
                            variant="filled"
                            color="secondary"
                            onClick={() => setIsModalOpen(false)}
                        >
                            Close
                        </CustomButton>
                    </div>
                }
            >
                {selectedInquiry && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', padding: '0.5rem 0' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Sender Name</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-primary)', fontWeight: 600 }}>
                                    <User size={14} color="var(--accent-primary)" />
                                    <span>{selectedInquiry.name}</span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Email Address</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                                    <Mail size={14} color="var(--accent-primary)" />
                                    <a href={`mailto:${selectedInquiry.email}`} style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}>
                                        {selectedInquiry.email}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>School / Organization</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                                    <Building size={14} color="var(--accent-primary)" />
                                    <span>{selectedInquiry.school_name || '-'}</span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Received On</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                                    <Calendar size={14} color="var(--accent-primary)" />
                                    <span>
                                        {new Date(selectedInquiry.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', padding: '0 0.25rem' }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Subject</span>
                            <span style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                                {selectedInquiry.subject || 'General Inquiry'}
                            </span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', borderTop: '1px dashed var(--border-color)', paddingTop: '1rem' }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Message Detail</span>
                            <div style={{
                                padding: '1rem',
                                borderRadius: '8px',
                                background: 'var(--bg-tertiary)',
                                border: '1px solid var(--border-color)',
                                color: 'var(--text-primary)',
                                fontSize: '0.9rem',
                                whiteSpace: 'pre-wrap',
                                lineHeight: '1.5',
                                minHeight: '120px'
                            }}>
                                {selectedInquiry.message}
                            </div>
                        </div>
                    </div>
                )}
            </CustomModal>
        </div>
    );
};
